import React, { ReactNode, useState } from 'react'
import { BottomDrawer, Button, InputGroup, Modal } from '@systemeio/components'
import { PlusCircleIcon, XCloseIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import { COMMUNITY_TITLE_MAX_LENGTH } from 'shared/constants'
import { BadRequest } from 'shared/errors/bad-request'
import { InternalError } from 'shared/errors/internal-error'
import { useIsMobileWhileOpen } from 'shared/hooks/use-is-mobile-while-open'
import { useCreateTopic } from '../api/use-create-topic'
import { generatePathFromName } from '../hooks/path-utils'
import { useCommunityWithTopics } from '../hooks/use-community-with-topics'

function AddTopic({
  className,
  icon,
}: {
  className?: string
  icon?: React.ComponentType
}): ReactNode {
  const { t } = useTranslation()
  const [opened, setOpened] = useState(false)
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState('')
  const [path, setPath] = useState('')
  const [pathError, setPathError] = useState('')
  const [error, setError] = useState('')
  const { data: community, mutate } = useCommunityWithTopics()
  const createTopicMutation = useCreateTopic()
  const isMobile = useIsMobileWhileOpen(opened)

  function changeName(e: React.ChangeEvent<HTMLInputElement>): void {
    setName(e.target.value)
    setNameError('')
  }

  function changePath(e: React.ChangeEvent<HTMLInputElement>): void {
    setPath(e.target.value)
    setPathError('')
  }

  function convertNameToPath(e: React.FocusEvent<HTMLInputElement>): void {
    if (!e.target.value) {
      setPath(generatePathFromName(name))
      setPathError('')
    }
  }

  const clearForm = (): void => {
    setName('')
    setPath('')
  }

  const handleSendForm = async (): Promise<void> => {
    if (!community) {
      return
    }
    try {
      const data = await createTopicMutation.trigger({
        name,
        path,
        community: community.id,
      })

      await mutate(
        {
          ...community,
          topics: [...community.topics, data],
        },
        false,
      )
      setOpened(false)
      clearForm()
    } catch (error_) {
      if (error_ instanceof BadRequest) {
        if (error_.errors?.fields?.path) {
          setPathError(error_.errors?.fields?.path.join('\n'))
        }
        if (error_.errors?.fields?.name) {
          setNameError(error_.errors?.fields?.name.join('\n'))
        }
        if (error_.errors.common && error_.errors.common.length > 0) {
          setError(error_.errors.common.join('\n'))
        }
      } else if (error_ instanceof InternalError) {
        setError(t('core.error.internal_error_message'))
      }
    }
  }

  const Icon = icon || PlusCircleIcon

  const title = t('settings.form.create_topic.title')

  const fields = (
    <>
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-text-primary">
          {t('settings.form.name.label')}
        </label>
        <InputGroup.Root data-invalid={!!nameError || undefined}>
          <InputGroup.Input
            value={name}
            onChange={changeName}
            maxLength={COMMUNITY_TITLE_MAX_LENGTH}
          />
        </InputGroup.Root>
        {nameError && <p className="text-sm text-text-error">{nameError}</p>}
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-text-primary">
          {t('settings.form.path.label')}
        </label>
        <InputGroup.Root data-invalid={!!pathError || undefined}>
          <InputGroup.Input
            value={path}
            onChange={changePath}
            onFocus={convertNameToPath}
            maxLength={32}
          />
        </InputGroup.Root>
        {pathError && <p className="text-sm text-text-error">{pathError}</p>}
      </div>
      {error && <p className="text-sm text-text-error">{error}</p>}
    </>
  )

  const action = (
    <Button variant="primary" onClick={handleSendForm}>
      <PlusCircleIcon className="size-5" />
      {t('settings.form.add_topic.label')}
    </Button>
  )

  return (
    <>
      <div className={twMerge('cursor-pointer text-text-primary', className)}>
        <Icon className="size-5" onClick={() => setOpened(true)} />
      </div>
      {isMobile ? (
        <BottomDrawer.Root open={opened} onOpenChange={value => setOpened(value)}>
          <BottomDrawer.Popup>
            <BottomDrawer.Header className="flex-row items-start gap-2.5">
              <BottomDrawer.Title className="min-w-0 flex-1">{title}</BottomDrawer.Title>
              <BottomDrawer.Close
                aria-label="Close"
                className="shrink-0"
                render={<Button variant="ghost-gray" size="icon-sm" />}
              >
                <XCloseIcon />
              </BottomDrawer.Close>
            </BottomDrawer.Header>
            <BottomDrawer.Content className="flex flex-col gap-6">{fields}</BottomDrawer.Content>
            <BottomDrawer.Footer>{action}</BottomDrawer.Footer>
          </BottomDrawer.Popup>
        </BottomDrawer.Root>
      ) : (
        <Modal.Root open={opened} onOpenChange={value => setOpened(value)}>
          <Modal.Popup>
            <Modal.Header>
              <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-8 pt-6 pb-2">
              {fields}
            </div>
            <Modal.Footer>{action}</Modal.Footer>
          </Modal.Popup>
        </Modal.Root>
      )}
    </>
  )
}

export default AddTopic
